import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import classes from './Expenses.module.scss';
import TNEDashboardHeader from '../../common/TNEDashboardHeader/TNEDashboardHeader';
import { useOnScreen, useQuery, useWindowSize, usePrevious } from '../../../utils/hooks';
import {
  appAreas,
  contractorExpensesFilters,
  globalPageSize,
  responsiveSizeTimesheet,
  approverExpensesFilters,
  timesheetsStatuses,
  appNotificationsTypes
} from '../../../utils/constants';
import CustomTable from '../../common/Table/CustomTable/CustomTable';
import TNECardsList from '../../common/TNECardsList/TNECardsList';
import { columns } from './helpers';
import { getExpenses, getNextExpenses, changeExpensesStatus, deleteClaim } from '../../../actions/expenses';
import StyledPaginationPanel from '../../common/StyledPaginationPanel/StyledPaginationPanel';
import { AddGradientIcon } from '../../common/Icons';
import { basicErrorHandler } from '../../../utils/helpers';
import MainButton from '../../common/buttons/MainButton';
import colors from '../../../scss/colors';
import BaseModal from '../../common/ModalWindows/BaseModal';
import {
  DeletePopUp,
  ApproveAllFinalModal,
  ConfirmationApproveAllModal
} from '../../common/ModalWindows/TNEModals/index';
import { tneAxiosClient } from '../../../api/apiSettings';
import WithCloseIcon from '../../common/ComposableWrappers/WithCloseIcon/WithCloseIcon';
import { getTandECountsContractor, getTandEPendingCounts, getTimeExpenseValidation } from '../../../actions/timesheet';
import { clearExpense } from '../../../actions/expense';
import TnEListSkeleton from '../../common/Skeletons/TnEListSkeleton';
import { getOptionsForFilters } from '../../../actions/filtersOptionsFromApi';
import { buttonVariants } from '../../common/buttons/types';
import AlternateMessageDialog from '../../common/AlternateMessageDialogComponent/AlternateMessageDialog';
import {
  setNotification,
  removeNotification,
  markExpenseNotificationsRead,
  shouldReadExpensesNotification
} from '../../../actions/appNotifications';
import { TNE_SORT_BY, generalEnums } from '../../../utils/enums';
import SuccessIcon from '../../common/Icons/SuccessIcon';

export default function Expenses() {
  const { t } = useTranslation();
  const size = useWindowSize();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery();

  const initIntersactionRequestRef = useRef(true);
  const IntersactionMarkerRef = useRef(null);
  const isIntersecting = useOnScreen(IntersactionMarkerRef);

  const expensesList = useSelector((state) => state?.expenses?.data);
  const paginationData = useSelector((state) => state.expenses.pagination);
  const infinityIsLoading = useSelector((state) => state.expenses.pendingInfinityScroll);
  const expensesPagination = useSelector((state) => state.expenses.pagination);
  const expensesPending = useSelector((state) => state.expenses.pending);
  const isApprover = useSelector((state) => state.accessToParts.tAndEApprover);
  const pendingTandECounts = useSelector((state) => state.timesheet?.tandecounts);
  const expenseValidationStatus = useSelector((state) => state?.timesheet?.validate?.expense);
  const filtersOptions = useSelector((state) => state.filtersOptionsFromApi.filters);
  const expensePlacementStatus = useSelector((state) => state?.timesheet?.validate?.timesheet.IsValidPlacementStatus);
  const hasExpensePendingCount = useSelector((state) => state.timesheet.hasExpensePendingCount);
  const isExpenseRead = useSelector((state) => state.appNotifications.isExpenseRead);

  const isApproveAllActive = useSelector((state) =>
    state?.expenses?.data?.some((exp) => exp.Status.Status === timesheetsStatuses(t).submitted.apiStatus)
  );
  const deletePending = useSelector((state) => state.expenses.pendingDelete);

  const filters = isApprover ? approverExpensesFilters : contractorExpensesFilters;
  const initPageNumber = Number(query.get('page')) || 1;
  const initDay = query.get('day') || '';
  const initMonth = query.get('month') || '';
  const initYear = query.get('year') || '';
  const initStatus = query.get('status') || '';
  const initClaim = query.get('claim') || '';
  const initFirstName = query.get('FirstName') || '';
  const initLastName = query.get('LastName') || '';
  const initSortBy = query.get('sortby') || [TNE_SORT_BY.PERIOD];
  const [active, setCurrentPage] = useState(initPageNumber);
  const [day, setDay] = useState(initDay);
  const [month, setMonth] = useState(initMonth);
  const [year, setYear] = useState(initYear);
  const [status, setStatus] = useState(initStatus);
  const [claim, setClaim] = useState(initClaim);
  const [contractor, setContractor] = useState({
    FirstName: initFirstName,
    LastName: initLastName
  });
  const [sortby, setSortBy] = useState([initSortBy]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [openPopUp, setOpenPop] = useState(false);
  const browserLocation = useLocation();
  const itemClickHandler = (id) => navigate(`/dashboard/expense/${id}${browserLocation.search}`);
  const userId = localStorage.getItem('UserId');
  const [lineToDelete, setLineToDelete] = useState('');
  const prevPendingDelete = usePrevious(deletePending);
  const [isFocus, setFocus] = useState(null);
  const itemFocusInHandler = (id) => {
    setFocus(id);
  };

  const itemFocusOutHandler = (id) => {
    setFocus(!id);
  };

  const handleSearchParams = () => {
    let oldSearchParams = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of searchParams.entries()) {
      oldSearchParams = {
        ...oldSearchParams,
        [key]: value
      };
    }

    return setSearchParams({
      ...oldSearchParams,
      page: active,
      day,
      month,
      year,
      status,
      claim,
      FirstName: contractor.FirstName,
      LastName: contractor.LastName,
      sortby
    });
  };

  const handlePagination = (num) => {
    setCurrentPage(num);
  };

  const handleMonth = (choosedMonth) => {
    setMonth(choosedMonth);
    if (typeof choosedMonth !== 'number') setYear('');
    if (choosedMonth && choosedMonth !== t('common.general.all')) setDay('');
  };

  const handleYear = (choosedYear, chosenMonth) => {
    if (choosedYear) {
      setYear(choosedYear);
      dispatch(
        getOptionsForFilters(
          appAreas.EXPENSES,
          isApprover,
          `${choosedYear}-${chosenMonth || '01'}-01`,
          '',
          status,
          claim
        )
      );
    }
  };

  const handleDay = (choosedDay) => {
    setDay(choosedDay);
    if (choosedDay) {
      handleMonth('');
      handleYear('');
    }

    dispatch(
      getOptionsForFilters(
        appAreas.EXPENSES,
        isApprover,
        choosedDay ? dayjs(choosedDay).format(generalEnums.DATEFORMAT) : '',
        '',
        status,
        claim
      )
    );
  };

  const handleStatus = (choosedStatus) => {
    setStatus(choosedStatus);
    dispatch(
      getOptionsForFilters(
        appAreas.EXPENSES,
        isApprover,
        year || month ? `${year}-${month || '01'}-01` : '',
        '',
        choosedStatus,
        claim
      )
    );
  };

  const handleClaim = (choosedClaim) => {
    setClaim(choosedClaim);
    dispatch(
      getOptionsForFilters(
        appAreas.EXPENSES,
        isApprover,
        year || month ? `${year}-${month || '01'}-01` : '',
        '',
        status,
        choosedClaim
      )
    );
  };

  const handleContractor = (choosedContractor) => {
    setContractor(choosedContractor);
  };

  const handleSortBy = (choosedSortBy) => {
    setSortBy(choosedSortBy);
  };

  const approveAll = async () => {
    const requestArr = [];
    expensesList.forEach((item) => {
      if (item.Status.Status === timesheetsStatuses(t).submitted.apiStatus) {
        requestArr.push({
          ExpenseId: item.Id,
          Status: timesheetsStatuses(t).approved.apiStatus
        });
      }
    });

    try {
      await tneAxiosClient.post(`v1/expense-approve-all`, requestArr);
      await dispatch(changeExpensesStatus(timesheetsStatuses(t).approved.apiStatus));
      navigate('/dashboard/expenses?page=1&day=&month=&year=&status=Pending&claim=&FirstName=&LastName=');
      setOpenPop({ modalType: 'approveAll' });
      setStatus('Pending');
      if (isApprover) {
        await dispatch(getTandEPendingCounts());
      } else {
        await dispatch(getTandECountsContractor());
      }
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [day, month, year, status, claim, contractor, sortby])

  useEffect(() => {
    dispatch(
      getExpenses(
        userId,
        active,
        globalPageSize,
        day,
        month,
        year,
        status,
        claim,
        contractor,
        sortby,
        t,
        isApprover,
        filtersOptions
      )
    );
    handleSearchParams();
  }, [active, day, month, year, status, claim, contractor, sortby]);

  useEffect(() => {
    dispatch(clearExpense());
    dispatch(getTimeExpenseValidation('', ''));
  }, []);

  const deleteHandler = (id) => {
    setOpenPop({ modalType: 'delete' });
    setLineToDelete(id);
  };

  const handleDeletePopupHandler = () => {
    dispatch(deleteClaim(lineToDelete));
    setOpenPop('');
  };

  const popUpModals = {
    delete: (
      <DeletePopUp
        deleteHandler={() => handleDeletePopupHandler()}
        closeHandler={() => {
          setOpenPop(false);
        }}
        withBackbtn={false}
        pending={deletePending}
      />
    ),
    approveAll: (
      <ApproveAllFinalModal
        message={t('dashboard.expenses.approvedsuccessfully')}
        refresh={() => dispatch(getExpenses(isApprover))}
        closeHandler={() => {
          setOpenPop(false);
        }}
      />
    ),
    approve: <ConfirmationApproveAllModal pendingTandECounts={pendingTandECounts} confirm={approveAll} />
  };

  useEffect(() => {
    if (
      isIntersecting &&
      size.width < responsiveSizeTimesheet &&
      !initIntersactionRequestRef.current &&
      paginationData &&
      paginationData.PageNumber + 1 <= paginationData?.TotalPages
    ) {
      dispatch(
        getNextExpenses(
          userId,
          Number(paginationData.PageNumber) + 1,
          globalPageSize,
          day,
          month,
          year,
          status,
          claim,
          contractor,
          sortby,
          t,
          isApprover,
          filtersOptions
        )
      );
    }
    initIntersactionRequestRef.current = false;
  }, [isIntersecting, expensesList]);

  useEffect(() => {
    if ((prevPendingDelete && !deletePending) || browserLocation.state?.idToDelete) {
      setOpenPop(false);
      dispatch(
        setNotification({
          isAppStyled: true,
          message: `${t('dashboard.expenses.expense')} ${t('common.general.deleted')}`,
          duration: 3000,
          type: appNotificationsTypes.SUCCESS,
          bottom: '160px',
          action: (
            <p
              role="presentation"
              className={classes.undoBtn}
              onClick={async () => {
                try {
                  await tneAxiosClient.get(
                    `/v1/expense/activate?expenseId=${browserLocation.state?.idToDelete || lineToDelete}`
                  );
                  dispatch(
                    getExpenses(
                      userId,
                      active,
                      globalPageSize,
                      day,
                      month,
                      year,
                      status,
                      claim,
                      contractor,
                      sortby,
                      t,
                      isApprover,
                      filtersOptions
                    )
                  );
                  dispatch(removeNotification());
                } catch (e) {
                  basicErrorHandler(dispatch, e.message);
                }
              }}
            >
              {t('common.general.undo')}
            </p>
          )
        })
      );
    }
  }, [prevPendingDelete, deletePending]);

  useEffect(() => {
    dispatch(getOptionsForFilters(appAreas.EXPENSES, isApprover, ''));
  }, []);

  useEffect(
    () => {
      if (isExpenseRead.enabled && expensesList) {
        dispatch(markExpenseNotificationsRead(userId, isExpenseRead.status));
        dispatch(shouldReadExpensesNotification({ enabled: false, status: null }));
      }
    },
    [isExpenseRead?.enabled],
    expensesList
  );

  const dateParser = (date) => {
    if (day) return day;
    if (year || month) return `${year}-${month}-01`;

    return date || '';
  };

  const dateBackAction = (date, claimParser = '', statusParser = '') => {
    dispatch(
      getOptionsForFilters(
        appAreas.EXPENSES,
        isApprover,
        dateParser(date),
        '',
        statusParser || status,
        claimParser || claim
      )
    );
  };

  const dateForwardAction = (date) => {
    dispatch(getOptionsForFilters(appAreas.EXPENSES, isApprover, date || ''));
  };

  if (!expensesPending && status === 'Pending' && hasExpensePendingCount)
    return (
      <section className={classes.mainContainer}>
        <TNEDashboardHeader
          title={size.width < responsiveSizeTimesheet ? t('dashboard.expenses.title') : ''}
          filters={filters}
          values={{
            day,
            month,
            year,
            status,
            claim,
            contractor,
            sortby
          }}
          changeHandlers={{
            handleDay,
            handleMonth,
            handleYear,
            handleStatus,
            handleClaim,
            handleContractor,
            dateBackAction,
            dateForwardAction,
            handleSortBy
          }}
          part={appAreas.EXPENSES}
        />
        <AlternateMessageDialog
          icon={<SuccessIcon />}
          headerLabel={t('dashboard.timesheets.greatJob')}
          startTracking={
            !isApprover
              ? t('dashboard.createtimesheet.starttrackingnow')
              : t('dashboard.createclaim.expenseawaitapproval')
          }
          mainButtonLabel={t('dashboard.expenses.showallclaims')}
          mainButtonEvent={() => setStatus('')}
          isApprover={isApprover}
        />
      </section>
    );

  return (
    <section className={classes.mainContainer}>
      <TNEDashboardHeader
        title={size.width < responsiveSizeTimesheet ? t('dashboard.expenses.title') : ''}
        filters={filters}
        values={{
          day,
          month,
          year,
          status,
          claim,
          contractor,
          sortby
        }}
        changeHandlers={{
          handleDay,
          handleMonth,
          handleYear,
          handleStatus,
          handleClaim,
          handleContractor,
          dateBackAction,
          dateForwardAction,
          handleSortBy
        }}
        part={appAreas.EXPENSES}
      />
      {size.width > responsiveSizeTimesheet ? (
        <CustomTable
          data={expensesList || []}
          columns={() => columns(t, isApprover, deleteHandler, isFocus)}
          withFooter={false}
          withLines={{
            header: false,
            body: false,
            footer: false
          }}
          bodyWithBorderRadius={false}
          rowClickHandler={itemClickHandler}
          rowFocusInHandler={itemFocusInHandler}
          rowFocusOutHandler={itemFocusOutHandler}
          wrapperStyles={{}}
          headerStyles={{
            borderRadius: '10px  10px 10px 10px',
            padding: '20px 40px 20px 88px'
          }}
          bodyStyles={{
            overflow: 'visible'
          }}
          lineStyles={{
            boxShadow: '0 0 30px rgba(61, 107, 149, 0.1)',
            borderRadius: '10px',
            height: '68px',
            marginTop: '12px',
            padding: '20px 40px 20px 88px'
          }}
          isLoading={expensesList === null}
          loadingComponent={<TnEListSkeleton qty={6} height="68px" gap="12px" />}
          isApprover={isApprover}
        />
      ) : (
        <div className={classes.cardListWrapper}>
          <TNECardsList
            type="expense"
            data={expensesList}
            clickHandler={itemClickHandler}
            isLoading={expensesList === null}
            isApprover={isApprover}
            loadingComponent={<TnEListSkeleton qty={10} height="90px" gap="16px" />}
          />
        </div>
      )}
      <div ref={IntersactionMarkerRef}>
        {infinityIsLoading && <CircularProgress sx={{ color: colors.NAVY_BLUE }} />}
      </div>
      {!isApprover && expensePlacementStatus && (
        <div className={classes.addButtonContainer}>
          <Link to="/dashboard/create-claim">
            <div className={classes.innerContainer}>
              {size.width > responsiveSizeTimesheet ? (
                <MainButton
                  variant={expenseValidationStatus ? 'primary' : 'disabled'}
                  disabled={!expenseValidationStatus}
                  rounded
                  forceWidth="max-content"
                  size="medium"
                >
                  {t('dashboard.expenses.newbuttontxt')}
                </MainButton>
              ) : (
                <AddGradientIcon size="50px" />
              )}
            </div>
          </Link>
        </div>
      )}
      {isApprover && pendingTandECounts?.PendingClaimsCount > 0 && (
        <div className={classes.approveButtonContainer}>
          <div className={classes.approveButton}>
            <MainButton
              rounded
              variant={isApproveAllActive ? buttonVariants.PRIMARY : buttonVariants.DISABLED}
              disabled={!isApproveAllActive}
              action={() => setOpenPop({ modalType: 'approve' })}
              forceWidth="100%"
              size="medium"
            >
              {t('dashboard.expenses.approveallbuttontxt')}
            </MainButton>
          </div>
        </div>
      )}
      {size.width > responsiveSizeTimesheet && (
        <div className={classes.paginationPanelContainer}>
          <StyledPaginationPanel
            total={expensesPagination?.TotalPages}
            active={active}
            setActive={(num) => handlePagination(num)}
          />
        </div>
      )}
      <BaseModal
        open={!!openPopUp}
        fullscreen={false}
        closeHandler={() => setOpenPop('')}
        title="&nbsp;"
        hideFooter
        hideHeader
        withSeparator={false}
        marginLeftRight={1}
        paperWidth={size.width > responsiveSizeTimesheet ? undefined : '100%'}
        paperStyles={
          size.width <= responsiveSizeTimesheet ? { margin: '15px', borderRadius: '10px' } : { borderRadius: '20px' }
        }
      >
        <WithCloseIcon close={() => setOpenPop('')}>{popUpModals[openPopUp?.modalType]}</WithCloseIcon>
      </BaseModal>
    </section>
  );
}
